import * as Sentry from "@sentry/react";
import { resolveAppEnvironment } from "@/lib/appEnvironment";

const environment = resolveAppEnvironment({
  configured: import.meta.env.VITE_APP_ENV,
  hostname: typeof window === "undefined" ? undefined : window.location.hostname,
  mode: import.meta.env.MODE,
});

Sentry.init({
  dsn: import.meta.env.VITE_SENTRY_DSN,
  integrations: [Sentry.browserTracingIntegration(), Sentry.replayIntegration()],
  // 20% of transactions on any real deployment. Note this used to read
  // `import.meta.env.PROD ? 0.2 : 1.0`, which happened to give staging
  // 0.2 as well — staging and production share one Sentry project, so
  // keying it on `environment` naively would have quietly sent staging
  // 5× more traces against the same quota.
  tracesSampleRate: environment === "development" ? 1.0 : 0.2,
  // Session replay: capture 10% of sessions, 100% of sessions with errors
  replaysSessionSampleRate: 0.1,
  replaysOnErrorSampleRate: 1.0,
  // One notion of "is this a real deployment", shared with the line
  // below. Previously `import.meta.env.PROD`, which also called a
  // production build previewed on localhost a real deployment.
  enabled: environment !== "development",
  // Resolved rather than taken from MODE — Vite calls every build
  // "production", which filed staging errors under production. See
  // lib/appEnvironment.ts.
  environment,
  // `AbortError: Lock broken by another request with the 'steal' option.`
  //
  // Comes from @supabase/auth-js, not from our code. It refreshes the
  // session under a Web Lock; when a lock is left orphaned (a component
  // unmounting mid-refresh, React Strict Mode's double mount, a second
  // tab) the library recovers by re-acquiring it with `{ steal: true }`.
  // Per the Web Locks spec that breaks the previous holder, whose
  // promise then rejects with this DOMException. Nobody awaits it — the
  // auto-refresh ticker calls `_autoRefreshTokenTick()` from a
  // `setInterval` without catching — so it surfaces as an unhandled
  // rejection and Sentry's global handler picks it up.
  //
  // The user sees nothing: the steal IS the recovery, and the session
  // refresh completes. Upstream references this as
  // supabase/supabase#42505 (the issue number is quoted in auth-js's own
  // source, node_modules/@supabase/auth-js/dist/main/lib/locks.js).
  // Filtered here rather than pinning or patching auth-js — the
  // mechanism is intended, only its reporting is wrong.
  //
  // Matched on the exact message, not on "AbortError", so the aborted
  // requests we DO want to see still come through.
  ignoreErrors: ["Lock broken by another request with the 'steal' option"],
});
